import * as React from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import ErrorIcon from '@material-ui/icons/Error'
import DoneIcon from '@mui/icons-material/Done'
import { useDispatch } from 'react-redux'
import { getQcReportAction } from '../../store/Workflow/actionCreator'

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: 600,
    padding: '24px 0',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px 16px',
  },
  headerContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flex: 1,
    paddingRight: 12,
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: 18,
    color: '#333',
  },
  disclaimer: {
    color: '#d32f2f',
    backgroundColor: '#fff3f3',
    border: '1px solid #f5c6c6',
    borderRadius: '4px',
    padding: '6px 10px',
    display: 'block',
    fontStyle: 'italic',
  },
  metaRow: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    padding: '0 24px 16px',
    flexWrap: 'wrap',
  },
  tableContainer: {
    padding: '0 24px',
  },
  headerCell: {
    fontWeight: 700,
    backgroundColor: '#fafafa',
    color: '#333',
  },
  emptyState: {
    padding: '40px 24px',
    textAlign: 'center',
    color: '#999',
  },
  loadingBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200,
  },
}))

const severityColor = (severity = '') => {
  switch (severity.toUpperCase()) {
    case 'HIGH':
      return {
        backgroundColor: '#fdecea',
        color: '#c62828',
        borderColor: '#c62828',
      }
    case 'MEDIUM':
      return {
        backgroundColor: '#fff3e0',
        color: '#e65100',
        borderColor: '#e65100',
      }
    case 'LOW':
      return {
        backgroundColor: '#e8f5e9',
        color: '#2e7d32',
        borderColor: '#2e7d32',
      }
    default:
      return { backgroundColor: '#f5f5f5', color: '#555', borderColor: '#bbb' }
  }
}

const statusColor = (status = '') => {
  switch (status.toUpperCase()) {
    case 'FAIL':
      return {
        backgroundColor: '#fdecea',
        color: '#c62828',
        borderColor: '#c62828',
      }
    case 'PASS':
      return {
        backgroundColor: '#e8f5e9',
        color: '#2e7d32',
        borderColor: '#2e7d32',
      }
    default:
      return { backgroundColor: '#f5f5f5', color: '#555', borderColor: '#bbb' }
  }
}

const WorkflowFlyoutQCStatus = ({ data = {} }) => {
  const { qcStatus = '', fileName = '', version = '' } = data
  const classes = useStyles()
  const dispatch = useDispatch()

  const [open, setOpen] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [report, setReport] = React.useState(null)

  const handleOpen = () => {
    setOpen(true)
    setLoading(true)
    setReport(null)
    dispatch(getQcReportAction(fileName, version))
      .then((data) => {
        if (data) setReport(data)
      })
      .finally(() => setLoading(false))
  }

  const handleClose = () => {
    setOpen(false)
    setReport(null)
  }

  if (!qcStatus) return 'N/A'
  console.log('ASdffa', qcStatus)
  return (
    <>
      <Chip
        label={qcStatus}
        size="small"
        icon={
          qcStatus === 'FAIL' ? (
            <ErrorIcon style={{ color: 'red' }} />
          ) : (
            <DoneIcon style={{ color: 'green' }} />
          )
        }
        onClick={handleOpen}
        style={
          qcStatus === 'FAIL'
            ? {
                backgroundColor: '#E0E0E0',
                color: 'red',
                cursor: 'pointer',
              }
            : {
                backgroundColor: '#E0E0E0',
                color: 'green',
                cursor: 'none',
                pointerEvents: 'none',
              }
        }
      />

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{ className: classes.drawerPaper }}
      >
        {/* Header */}
        <Box className={classes.header}>
          <Box className={classes.headerContent}>
            <Typography className={classes.headerTitle}>
              Infographics QC Report
            </Typography>
            <Typography variant="caption" className={classes.disclaimer}>
              ⚠️ <strong>Disclaimer:</strong> This report is AI-generated and
              should be used for reference only. It does not block any
              functionalities in Photobridge.
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />

        {loading ? (
          <Box className={classes.loadingBox}>
            <CircularProgress size={32} />
          </Box>
        ) : report ? (
          <>
            {/* Meta info */}
            <Box className={classes.metaRow} style={{ marginTop: 16 }}>
              <Typography variant="body2" style={{ color: '#555' }}>
                <strong>File:</strong> {report.file_name}
              </Typography>
              <Typography variant="body2" style={{ color: '#555' }}>
                <strong>Version:</strong> {report.version}
              </Typography>
              <Chip
                label={report.status}
                size="small"
                variant="outlined"
                style={{ ...statusColor(report.status), fontWeight: 700 }}
              />
              <Chip
                label={`Severity: ${report.severity}`}
                size="small"
                variant="outlined"
                style={{ ...severityColor(report.severity), fontWeight: 700 }}
              />
            </Box>
            <Divider />

            {/* Findings table */}
            <Box className={classes.tableContainer} style={{ marginTop: 16 }}>
              <Typography
                variant="subtitle2"
                style={{ fontWeight: 700, marginBottom: 8, color: '#333' }}
              >
                Findings ({(report.findings || []).length})
              </Typography>
              {(report.findings || []).length === 0 ? (
                <Typography variant="body2" className={classes.emptyState}>
                  No findings found.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          className={classes.headerCell}
                          style={{ width: 80 }}
                        >
                          Description
                        </TableCell>
                        <TableCell className={classes.headerCell}>
                          Expected
                        </TableCell>
                        <TableCell className={classes.headerCell}>
                          Observed
                        </TableCell>
                        <TableCell
                          className={classes.headerCell}
                          style={{ width: 90 }}
                        >
                          Confidence
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.findings.map((finding, idx) => (
                        <TableRow
                          key={idx}
                          hover
                          style={{
                            backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa',
                          }}
                        >
                          <TableCell>
                            <Chip
                              label={finding.description}
                              size="small"
                              style={{
                                backgroundColor: '#fdecea',
                                color: '#c62828',
                                fontWeight: 700,
                                fontSize: 11,
                              }}
                            />
                          </TableCell>
                          <TableCell style={{ fontSize: 12, color: '#333' }}>
                            {finding.expected}
                          </TableCell>
                          <TableCell style={{ fontSize: 12, color: '#c62828' }}>
                            {finding.observed}
                          </TableCell>
                          <TableCell
                            style={{ fontSize: 12, textAlign: 'center' }}
                          >
                            {(finding.confidence * 100).toFixed(0)}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </>
        ) : (
          <Typography variant="body2" className={classes.emptyState}>
            No report data available.
          </Typography>
        )}
      </Drawer>
    </>
  )
}

export default WorkflowFlyoutQCStatus
